/** @file This file contains utilities to work with errors. */
/**
 * Throws `caught` unless it is an instance of at least one of the given error classes.
 *
 * Use this at the top of a `catch` block or inside a `.catch()` arrow to propagate unexpected
 * errors while silently swallowing known, expected ones.
 *
 * @example
 * // Swallow one expected error class:
 * } catch (caught) {
 *     errors.rethrowUnless(caught, ConditionalCheckFailedError);
 * }
 *
 * @example
 * // In a .catch() handler:
 * await op().catch(caught => errors.rethrowUnless(caught, ConditionalCheckFailedError));
 *
 * @example
 * // Swallow multiple expected error classes:
 * } catch (caught) {
 *     errors.rethrowUnless(caught, ErrorA, ErrorB);
 * }
 *
 * @param caught The value thrown (usually from a `catch` block).
 * @param first The first (required) class to test against.
 * @param rest Any additional classes to test against.
 */
export function rethrowUnless(caught, first, ...rest) {
    if (!(caught instanceof first) && !rest.some(Cls => caught instanceof Cls)) {
        throw caught;
    }
}
/**
 * Convert a value to an error.
 *
 * If the given value is already an error it will be returned as is, if the value is `null` we
 * will provide a special message.
 *
 * @param x The value that should be converted to an error.
 * @returns The converted error.
 */
export function coerceToError(x) {
    if (x instanceof Error) {
        return x;
    }
    else if (x == null) {
        return new Error('Got an undefined value as error');
    }
    else {
        return new Error(String(x));
    }
}
